import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { Chat, GeniiusUser } from '@/lib/types'
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store';
import { getChatsFromExternalDb, getChatsAfterDate } from '@/app/actions/chats';
import { logger } from '@/lib/logger'
import { customStorage } from '@/lib/hooks/zustand-stores/utils'

export const MAX_CACHE_SIZE = 100;
export const BATCH_SIZE = 25;

// Create a logger with store context
const getStoreLogger = () => {
  const user = useUserStore.getState().user;
  const userId = user?._id || null;
  const userFullName = user?.fullName || null;

  const additionalContext = {
    userId: userId,
    userFullName: userFullName,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
    os: typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
    level: 'info',
    timestamp: new Date().toISOString()
  };

  const isProduction = process.env.NODE_ENV === 'production';
  return isProduction
    ? logger.child({ ...additionalContext, level: 'silent' })
    : logger.child(additionalContext);
};

interface ChatListState {
  chatList: Chat[];
  loading: boolean;
  loadingMoreChats: boolean;
  hasMore: boolean;
  currentPage: number;
  initialLoadDone: boolean;
  lastSynced: string | null;
  isSyncing: boolean;
  organizationId: string | null;
}

interface ChatListActions {
  loadMoreChats: (fromTimout?: boolean) => Promise<void>;
  syncChats: () => Promise<void>;
  refreshChatInList: (chatId: string) => Promise<void>;
  addToChatList: (chat: Chat) => void;
  updateChat: (chat: Chat) => void;
  deleteChatFromList: (chatId: string) => void;
  clearStore: () => void;
  initializeStore: () => Promise<void>;
  updateChatField: <K extends keyof Chat>(chatId: string, field: K, value: Chat[K]) => void;
  updateChatTimestamp: (chatId: string) => void;
  updateChatLastMessage: (chatId: string, userInfo: Partial<GeniiusUser>, content: string) => void
  refreshChatList: () => Promise<void>;
  setOrganizationId: (organizationId: string | null) => void;
}


const useChatListStore = create<ChatListState & ChatListActions>()(
  devtools(
    persist(
      (set, get) => ({
        chatList: [],
        loading: false,
        loadingMoreChats: false,
        hasMore: true,
        currentPage: 1,
        initialLoadDone: false,
        lastSynced: null,
        isSyncing: false,
        organizationId: null,

        setOrganizationId: (organizationId: string | null) => {
          set({ organizationId });
          // Clear the chat list when organization changes
         get().clearStore();
          // Reinitialize with new organization
          get().initializeStore();
        },

        syncChats: async () => {
          const mixusUserId = useUserStore.getState().user?._id;
          const organizationId = get().organizationId;
          if (!mixusUserId) return;

          set({ isSyncing: true });
          try {
            // Get chats that were updated after last sync
            const { chats: newChats } = await getChatsAfterDate(mixusUserId, get().lastSynced, organizationId);
            set((state) => {
              const combinedChats = [...state.chatList, ...newChats];
              const uniqueChats = Array.from(
                new Map(combinedChats.map((chat) => [chat._id, chat])).values()
              );
              const sortedUniqueChats = filterAndSortChats(uniqueChats);

              return {
                chatList: sortedUniqueChats,
                isSyncing: false,
                lastSynced: new Date().toISOString()
              };
            });
          } catch (error) {
            getStoreLogger().error(`Error syncing chats: ${error}`);
            set({ isSyncing: false });
          }
        },

        refreshChatList: async () => {
          const mixusUserId = useUserStore.getState().user?._id;
          if (!mixusUserId) return;

          set({ loading: true });
          get().clearStore();
          await get().initializeStore();
          set({ loading: false });
        },

        loadMoreChats: async (fromTimout?: boolean) => {
          if (fromTimout) getStoreLogger().info(`loaded chat list from timout: ${fromTimout}`)
          const { currentPage, loading, loadingMoreChats, hasMore, organizationId } = get();
          getStoreLogger().info(`currentPage: ${currentPage}, loadingMoreChats: ${loadingMoreChats} hasMore: ${hasMore}`)
          const mixusUserId = useUserStore.getState().user?._id;
          if (!mixusUserId || !hasMore || loadingMoreChats) return;

          set({ loading: true, loadingMoreChats: true });

          try {
            const newChats = await getChatsFromExternalDb(mixusUserId, undefined, currentPage, BATCH_SIZE, true, organizationId);

            set((state) => {
              const combinedChats = [...state.chatList, ...newChats];
              const uniqueChats = Array.from(
                new Map(combinedChats.map((chat) => [chat._id, chat])).values()
              );
              const sortedChats = filterAndSortChats(uniqueChats);
              const sortedUniqueChats = Array.from(
                new Map(sortedChats.map(chat => [chat._id, chat])).values()
              );

              return {
                chatList: sortedUniqueChats,
                hasMore: newChats.length === BATCH_SIZE,
                currentPage: currentPage + 1,
                loading: false,
                loadingMoreChats: false,
              };
            });
          } catch (error) {
            getStoreLogger().error(`Error loading chats: ${error}`);
            set({ loading: false, loadingMoreChats: false });
          }
        },

        refreshChatInList: async (chatId: string) => {
          const mixusUserId = useUserStore.getState().user?._id;
          const organizationId = get().organizationId;
          if (!mixusUserId) return;

          try {
            const updatedChat = await getChatsFromExternalDb(mixusUserId, chatId, undefined, undefined, undefined, organizationId);

            set((state) => {
              const existingChatIndex = state.chatList.findIndex((chat) => chat._id === chatId);
              let updatedList;

              if (existingChatIndex !== -1) {
                updatedList = state.chatList.map((chat) => (chat._id === chatId ? updatedChat[0] : chat));
              } else {
                updatedList = [...state.chatList, updatedChat[0]];
              }

              const filteredAndSortedList = filterAndSortChats(updatedList);
              return {
                chatList: filteredAndSortedList,
              };
            });
          } catch (error) {
            getStoreLogger().error(`Error refreshing chat: ${error}`);
          }
        },

        initializeStore: async () => {
          try {
            getStoreLogger().info('Starting chat list initializeStore...');
            const mixusUserId = useUserStore.getState().user?._id;
            getStoreLogger().info(`User ID: ${mixusUserId}`);

            if (!mixusUserId || get().initialLoadDone) {
              getStoreLogger().info(`Skipping initialization: ${!mixusUserId ? 'No user ID' : 'Already initialized'}`);
              return;
            }

            set({ loading: true });
            getStoreLogger().info(`Current chat list length: ${get().chatList.length}`);

            // If no cache, load initial batch
            if (get().chatList.length === 0) {
              getStoreLogger().info('Loading initial batch of chats...');
              const newChats = await getChatsFromExternalDb(mixusUserId, undefined, 1, BATCH_SIZE, true);
              getStoreLogger().info(`Loaded initial chats: ${newChats.length}`);
              set(state => ({
                chatList: newChats,
                hasMore: newChats.length === BATCH_SIZE,
                lastSynced: new Date().toISOString() // Set lastSynced when loading initial batch
              }));
            } else {
              // Only sync if we have existing chats and haven't synced recently
              const lastSynced = get().lastSynced;
              getStoreLogger().info(`Last synced: ${lastSynced}`);
              if (!lastSynced || Date.now() - new Date(lastSynced).getTime() > 60 * 1000) {
                getStoreLogger().info('Starting chat sync...');
                await get().syncChats();
                getStoreLogger().info('Chat sync completed');
              } else {
                getStoreLogger().info('Skipping sync - recently synced');
              }
            }

            set({ initialLoadDone: true, loading: false });
            getStoreLogger().info('Store initialization complete');
          } catch (e) {
            getStoreLogger().error(`Error in initializeStore: ${e}`);
            set({ initialLoadDone: true, loading: false });
          }
        },

        addToChatList: (chat: Chat) => {
          set((state) => {
            const updatedList = [chat, ...state.chatList];
            const filteredList = filterAndSortChats(updatedList);

            return {
              chatList: filteredList,
            };
          });
        },

        updateChat: (newChat: Chat) => {
          set((state) => {
            const updatedList = state.chatList.map((chat) => (chat._id === newChat._id ? newChat : chat));
            const filteredList = filterAndSortChats(updatedList);

            return { chatList: filteredList };
          });
        },

        deleteChatFromList: (chatId: string) => {
          set((state) => {
            const updatedList = state.chatList.filter((chat) => chat._id !== chatId);

            return {
              chatList: updatedList,
            };
          });
        },

        clearStore: () => {
          localStorage.removeItem('chat-list-storage');
          set({
            chatList: [],
            hasMore: true,
            currentPage: 1,
            initialLoadDone: false,
            lastSynced: null,
          });
        },

        updateChatField: <K extends keyof Chat>(chatId: string, field: K, value: Chat[K]) => {
          set((state) => {
            const updatedList = state.chatList.map((chat) => (chat._id === chatId ? { ...chat, [field]: value } : chat));
            const filteredList = filterAndSortChats(updatedList);

            return { chatList: filteredList };
          });
        },

        updateChatTimestamp: (chatId: string) => {
          set((state) => {
            const updatedList = state.chatList.map((chat) =>
              chat._id === chatId
                ? { ...chat, updatedAt: new Date().toISOString(), lastMessageAt: new Date().toISOString() }: chat
            );
            const filteredList = filterAndSortChats(updatedList as Chat[]);

            return { chatList: filteredList };
          });
        },

        updateChatLastMessage: (chatId: string, userInfo: Partial<GeniiusUser>, content: string) => {
          set((state) => {
            const updatedList = state.chatList.map((chat) =>
              chat._id === chatId
                ? { ...chat, chatPreviewLastMessage: {createdAt: new Date().toISOString(), userInfo, content} }: chat
            );
            const filteredList = filterAndSortChats(updatedList as Chat[]);
            return { chatList: filteredList };
          });
        },
      }),
      {
        name: 'chat-list-storage',
        version: 1,
        storage: createJSONStorage(() => customStorage as any),
        partialize: (state) => ({
          organizationId: state.organizationId,
          chatList: state.chatList
            .slice(0, MAX_CACHE_SIZE)
            .map(extractCacheableFields),
          lastSynced: state.lastSynced,
        }),
      }
    )
  )
);

// Helper functions
const filterAndSortChats = (chats: Chat[]) => {
  return chats
    .filter((chat) => chat && chat.title)
    .sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      const dateA = new Date(a.updatedAt || a.createdAt);
      const dateB = new Date(b.updatedAt || b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
};

// Function to extract only necessary fields for caching
const extractCacheableFields = (chat: Chat) => {
  if (!chat) return null;

  // Helper function to extract minimal user details
  const extractMinimalUserDetails = (user: any) => {
    if (!user) return null;
    return {
      _id: user._id,
      username: user.username,
      imageUrl: user.imageUrl,
      firstName: user.firstName,
      fullName: user.fullName,
      lastName: user.lastName
    };
  };

  return {
    _id: chat._id,
    title: chat.title,
    createdAt: chat.createdAt,
    updatedAt: chat.updatedAt,
    lastMessageAt: chat.lastMessageAt,
    isPinned: chat.isPinned,
    isUnread: chat.isUnread,
    isPublic: chat.isPublic,
    path: chat.path,
    publicPath: chat.publicPath,
    imageUrl: chat.imageUrl,
    isPublishedToCommunity: chat.isPublishedToCommunity,
    model: chat.model,
    provider: chat.provider,
    userId: chat.userId,
    allowJoinRequests: chat.allowJoinRequests,
    chatPreviewLastMessage: chat.chatPreviewLastMessage ? {
      content: chat.chatPreviewLastMessage.content,
      createdAt: chat.chatPreviewLastMessage.createdAt,
      userInfo: chat.chatPreviewLastMessage.userInfo ? extractMinimalUserDetails(chat.chatPreviewLastMessage.userInfo) : null
    } : null,
    userInfo: chat.userInfo ? extractMinimalUserDetails(chat.userInfo) : null,
    permittedUsersDetails: chat.permittedUsersDetails?.map(extractMinimalUserDetails) || [],
    invitedUsersDetails: chat.invitedUsersDetails?.map(extractMinimalUserDetails) || [],
  };
};

export default useChatListStore;
